import { apiService } from '../api/api-client';

export interface MarketQuote {
  symbol_id: string;
  price: number;
  fetched_at: string;
}

interface MarketQuoteRawResponse {
  symbol_id: string;
  price: string;
  fetched_at: string;
}

export interface MarketCob {
  symbol_id: string;
  cob_price: number;
  fetched_at: string;
}

interface MarketCobRawResponse {
  symbol_id: string;
  cob_price: string;
  fetched_at: string;
}

// LR-378: soft-failing TickDB quote lookup used by Trade Entry form.
// Returns null on any error (TickDB miss → 404, network error, anything) so
// the form can fall back to "enter manually" without surfacing toasts.
export const marketDataService = {
  async fetchQuote(symbolId: string): Promise<MarketQuote | null> {
    if (!symbolId) return null;
    try {
      const raw = await apiService.get<MarketQuoteRawResponse>(
        '/market-data/quote/',
        { params: { symbol_id: symbolId } },
      );
      return { ...raw, price: parseFloat(raw.price) };
    } catch {
      return null;
    }
  },

  // LR-578: canonical COB T-1 close — the same source the Trades blotter shows
  // as "COB Gross Price". Same soft-fail contract as fetchQuote: null on 404
  // (no COB for symbol) or any error, so Gross Price keeps the synchronous
  // inventory-snapshot prefill as its fallback. A non-positive close (a
  // degenerate 0 candle) is treated as "no COB" too — overwriting a usable
  // inventory price with 0.00 would only trap the required-field validator
  // (review r2); the blotter may still display such a close, but it is not a
  // bookable prefill.
  async fetchCob(symbolId: string): Promise<MarketCob | null> {
    if (!symbolId) return null;
    try {
      const raw = await apiService.get<MarketCobRawResponse>(
        '/market-data/cob/',
        { params: { symbol_id: symbolId } },
      );
      const cobPrice = parseFloat(raw.cob_price);
      if (!Number.isFinite(cobPrice) || cobPrice <= 0) return null;
      return { ...raw, cob_price: cobPrice };
    } catch {
      return null;
    }
  },
};
